import { Loader2, RefreshCw, AlertTriangle, Info } from 'lucide-preact'
import { assetApi } from '@/api/asset'
import { useEmailPreviewFetch } from '@/hooks/useEmailPreviewFetch'

interface EmailTemplatePreviewProps {
    clientCompanyId: string
    workspaceId: string
    campaignId: string
    templateAssetId: string
    /** Render this exact HTML (a past version's stored template HTML) through the substituting
     *  preview instead of the asset's current DraftHtml. Null/undefined previews the current draft. */
    htmlOverride?: string | null
    /** Re-render whenever this changes. Used by the parent (Asset Review) to force a refresh
     *  after Webster patches the template HTML server-side. */
    refreshKey?: number
}

/**
 * Read-only preview of an Email Template asset rendered with placeholder body/CTA — the
 * Asset Review surface uses this in place of the editable Tiptap canvas so the user sees
 * the same HTML that would actually be sent (with the AI's table-based centering intact).
 *
 * Mirrors EmailCopyPreview's iframe pattern (sandboxed srcDoc, refresh button, warning
 * banner) so the two read-only previews feel identical across the dashboard.
 */
export function EmailTemplatePreview({
    clientCompanyId,
    workspaceId,
    campaignId,
    templateAssetId,
    htmlOverride,
    refreshKey = 0,
}: EmailTemplatePreviewProps) {
    const { preview, error, loading, refresh } = useEmailPreviewFetch(
        signal => assetApi.previewEmailTemplate(
            clientCompanyId, workspaceId, campaignId, templateAssetId,
            htmlOverride ? { HtmlOverride: htmlOverride } : undefined, signal,
        ),
        `${clientCompanyId}|${workspaceId}|${campaignId}|${templateAssetId}|${htmlOverride ?? ''}|${refreshKey}`,
    )

    return (
        <div className="flex flex-col h-full bg-slate-100 dark:bg-slate-950">
            {error && (
                <div className="flex-shrink-0 flex items-start gap-2 px-4 py-2 bg-red-50 dark:bg-red-900/20 border-b border-red-200 dark:border-red-800 text-xs text-red-700 dark:text-red-300">
                    <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                    <span>{error}</span>
                </div>
            )}
            {preview?.Warning && (
                <div className="flex-shrink-0 flex items-start gap-2 px-4 py-2 bg-amber-50 dark:bg-amber-900/20 border-b border-amber-200 dark:border-amber-800 text-xs text-amber-800 dark:text-amber-300">
                    <Info className="w-4 h-4 flex-shrink-0 mt-0.5" />
                    <span>{preview.Warning}</span>
                </div>
            )}

            <div className="flex-shrink-0 flex items-center justify-between gap-2 px-4 py-2 border-b border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900">
                <div className="min-w-0 flex-1">
                    <div className="text-[10px] uppercase tracking-wide text-slate-400 dark:text-slate-500">Template Preview</div>
                    <div className="truncate text-sm font-medium text-slate-900 dark:text-slate-100">
                        {preview?.Subject || <span className="italic text-slate-400">Loading…</span>}
                    </div>
                </div>
                <button
                    type="button"
                    onClick={refresh}
                    title="Refresh preview"
                    className="inline-flex items-center justify-center w-7 h-7 rounded-md text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800"
                >
                    <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
                </button>
            </div>

            <div className="flex-1 relative overflow-hidden">
                {preview && (
                    <iframe
                        title="Email template preview"
                        srcDoc={preview.HtmlBody}
                        sandbox=""
                        className="w-full h-full border-0 bg-white"
                    />
                )}

                {loading && !preview && (
                    <div className="absolute inset-0 flex items-center justify-center">
                        <Loader2 className="w-5 h-5 animate-spin text-slate-400" />
                    </div>
                )}
            </div>
        </div>
    )
}
